import React from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AdminLayoutProps {}

export function AdminLayout({}: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    return localStorage.getItem('admin_sidebar_collapsed') === 'true';
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('admin_sidebar_collapsed', String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/admin/clients', label: 'Clientes', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { path: '/admin/subscribers', label: 'Suscriptores', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { path: '/admin/pdfs', label: 'Adjuntos', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { path: '/admin/news', label: 'Noticias URLs', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z' },
    { path: '/admin/comunicados-directos', label: 'Comunicados', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
    { path: '/admin/comunicados', label: 'Envíos', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 overflow-x-hidden">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 bg-gray-900 text-white transform transition-all duration-300 z-50 lg:translate-x-0 ${
          collapsed ? 'lg:w-20' : 'lg:w-64'
        } w-64 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo & Toggle */}
          <div className={`flex items-center justify-between px-4 h-16 border-b border-gray-800 bg-gray-950/40 ${collapsed ? 'flex-col py-3 justify-center gap-2' : ''}`}>
            <Link to="/admin" className="flex items-center gap-2 font-bold animate-pulse" onClick={() => setMobileMenuOpen(false)}>
              <img src="/logo.png" alt="Congroup Logo" className="h-9 w-auto object-contain bg-white rounded p-1" />
              {!collapsed && <span className="text-lg font-bold text-white leading-none">Congroup</span>}
            </Link>
            <button
              onClick={toggleCollapsed}
              className="hidden lg:flex p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
              title={collapsed ? "Expandir menú" : "Colapsar menú"}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {collapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center text-gray-300 rounded-lg hover:bg-gray-800 hover:text-white transition-colors ${
                  collapsed ? 'justify-center p-3' : 'px-4 py-3'
                }`}
                title={collapsed ? item.label : undefined}
                onClick={() => setMobileMenuOpen(false)}
              >
                <svg className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {!collapsed && <span className="truncate text-sm">{item.label}</span>}
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-gray-800">
            <div className={`flex items-center ${collapsed ? 'justify-center mb-3' : 'mb-4'}`}>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0 shadow-md">
                <span className="text-sm font-semibold">
                  {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'A'}
                </span>
              </div>
              {!collapsed && (
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-white">{user?.displayName || 'Admin'}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
              )}
            </div>
            <button
              onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
              className={`flex items-center justify-center text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors w-full ${
                collapsed ? 'p-3' : 'px-4 py-2 text-sm'
              }`}
              title={collapsed ? "Cerrar Sesión" : undefined}
            >
              <svg className={`w-5 h-5 ${collapsed ? '' : 'mr-2'} shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {!collapsed && <span>Cerrar Sesión</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header Bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-gray-900 text-white flex items-center justify-between px-4 z-40 shadow-md">
        <Link to="/admin" className="flex items-center gap-2 font-bold" onClick={() => setMobileMenuOpen(false)}>
          <img src="/logo.png" alt="Congroup Logo" className="h-9 w-auto object-contain bg-white rounded p-1" />
          <span className="text-lg font-bold text-white">Congroup</span>
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-gray-300 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-45 transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Main content */}
      <main className={`transition-all duration-300 ${collapsed ? 'lg:ml-20' : 'lg:ml-64'} min-h-screen pt-16 lg:pt-0 overflow-x-hidden`}>
        <div className="p-3 lg:p-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default AdminLayout;